import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../landing/AuthContext'
import { useTheme } from '../../context/ThemeContext'
import { Ic, Avatar } from '../../components/ui'
import { useDashboardProjects } from './hooks/useDashboardProjects'
import { useDashboardToast } from './hooks/useDashboardToast'
import { authenticatedJsonRequest } from '../../shared/auth/authenticatedFetch'
import { DK, LT } from './theme'
import { navigateToNewProject } from '../../shared/navigation/navigateToNewProject'
import ProfileTab from './tabs/ProfileTab'
import ProjectsTab from './tabs/ProjectsTab'
import SettingsTab from './tabs/SettingsTab'
import AboutTab from './tabs/AboutTab'
import ReviewTab from './tabs/ReviewTab'

const NAV = [
  { key: 'profile',  label: 'Мой профиль',  icon: 'user'   },
  { key: 'projects', label: 'Проекты',       icon: 'folder' },
  { key: 'settings', label: 'Настройки',     icon: 'cog'    },
  { key: 'about',    label: 'О компании',    icon: 'info'   },
  { key: 'review',   label: 'Написать отзыв',icon: 'chat'   },
]

export default function DashboardPage() {
  const { user, isAuthenticated, logout, login, token } = useAuth()
  const t = useTheme()
  const c = t.dark ? DK : LT
  const navigate = useNavigate()
  const location = useLocation()
  const [tab, setTab] = useState(() => {
    const p = new URLSearchParams(location.search).get('tab')
    return (p && NAV.some(x => x.key === p)) ? p : 'profile'
  })
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [acctMenu, setAcctMenu] = useState(false)
  useEffect(() => {
    const p = new URLSearchParams(location.search).get('tab')
    if (p && NAV.some(x => x.key === p)) setTab(p)
  }, [location.search])

  const { toast, triggerToast, dismissToast } = useDashboardToast()

  const handleSessionInvalid = useCallback(() => { logout(); navigate('/auth') }, [logout, navigate])

  const {
    projects, projectsLoading, projectNumberMap, resolveAssetUrl,
    handleDeleteProject, downloadProjectPdf, pdfDownloadingId,
  } = useDashboardProjects({ currentTab: tab, isAuthenticated, token, triggerToast, dismissToast, onSessionInvalid: handleSessionInvalid })

  const handleProfileUpdate = useCallback(async (data) => {
    try {
      const res = await authenticatedJsonRequest('/api/auth/me', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
      })
      if (res?.token) await login(res.token, { user: res.user })
      triggerToast('Профиль обновлён', 'ok')
    } catch (e) { triggerToast(e.message || 'Ошибка сохранения', 'error') }
  }, [login, triggerToast])

  const handleLogout = useCallback(() => { logout(); navigate('/') }, [logout, navigate])
  const goTab = (key) => { setTab(key); setDrawerOpen(false) }
  const tabTitle = NAV.find(x => x.key === tab)?.label || ''

  const renderSidebar = () => (
    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
      {/* Logo */}
      <div style={{ padding:'22px 20px 16px', display:'flex', alignItems:'center', gap:11 }}>
        <img src="/logo.png" alt="Welland" style={{ width:38, height:38, objectFit:'contain', flexShrink:0 }}/>
        <span style={{ fontFamily:'var(--font-main)', display:'inline-flex', alignItems:'baseline', gap:7 }}>
          <span style={{ fontWeight:900, fontSize:17, letterSpacing:'0.06em', color:c.white }}>WELLAND</span>
          <span style={{ fontWeight:600, fontSize:11.5, letterSpacing:'0.18em', color:c.teal }}>CREATE</span>
        </span>
      </div>
      {/* Gradient divider */}
      <div style={{ height:1, margin:'0 20px 12px', background:t.dark ? 'linear-gradient(90deg,rgba(31,200,214,.45),rgba(31,200,214,.1) 70%,transparent)' : 'linear-gradient(90deg,rgba(10,123,134,.35),rgba(10,123,134,.08) 70%,transparent)' }}/>

      <nav style={{ flex:1, padding:'4px 10px', overflowY:'auto' }}>
        {NAV.map(item => {
          const active = tab === item.key
          return (
            <button key={item.key} onClick={() => goTab(item.key)} style={{
              display:'flex', alignItems:'center', gap:12,
              padding:'10px 14px', borderRadius:12, width:'100%', cursor:'pointer',
              border: active ? '1px solid rgba(15,163,177,.28)' : '1px solid transparent',
              background: active
                ? 'linear-gradient(135deg,rgba(15,163,177,.18) 0%,rgba(10,123,134,.08) 100%)'
                : 'transparent',
              color: active ? c.tealBright : c.muted,
              fontSize:14, fontWeight: active ? 700 : 600, transition:'all .18s',
              textAlign:'left', fontFamily:"'Plus Jakarta Sans','Manrope',sans-serif",
              boxShadow: active ? '0 2px 14px rgba(15,163,177,.12)' : 'none',
              transform:'translateX(0)', marginBottom:2,
            }}
            onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'rgba(15,163,177,.07)'; e.currentTarget.style.color = c.text; e.currentTarget.style.transform = 'translateX(2px)' }}}
            onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = c.muted; e.currentTarget.style.transform = 'translateX(0)' }}}>
              <div style={{
                width:32, height:32, borderRadius:9, flexShrink:0,
                background: active
                  ? 'linear-gradient(135deg,rgba(31,200,214,.25),rgba(10,123,134,.15))'
                  : t.dark ? 'rgba(255,255,255,.04)' : 'rgba(10,32,40,.05)',
                border: active ? '1px solid rgba(31,200,214,.3)' : '1px solid transparent',
                display:'flex', alignItems:'center', justifyContent:'center',
                color: active ? c.tealBright : c.muted, transition:'all .18s',
              }}>
                <Ic n={item.icon} s={15}/>
              </div>
              {item.label}
            </button>
          )
        })}
      </nav>

      {/* Bottom section */}
      <div style={{ padding:'8px 10px 20px' }}>
        <div style={{ height:1, margin:'0 6px 12px', background:t.dark ? 'linear-gradient(90deg,transparent,rgba(255,255,255,.08),transparent)' : 'linear-gradient(90deg,transparent,rgba(10,32,40,.1),transparent)' }}/>

        {/* User card */}
        <div style={{
          display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:14, marginBottom:4,
          background: t.dark ? 'rgba(31,200,214,.06)' : 'rgba(15,163,177,.06)',
          border:`1px solid ${t.dark ? 'rgba(31,200,214,.12)' : 'rgba(15,163,177,.14)'}`,
        }}>
          <div style={{ position:'relative', flexShrink:0 }}>
            <Avatar name={user?.name || ''} s={34}/>
            <span style={{ position:'absolute', bottom:0, right:0, width:8, height:8, borderRadius:'50%', background:'#22C55E', border:`2px solid ${t.dark ? c.bg1 : '#fff'}` }}/>
          </div>
          <div style={{ minWidth:0, flex:1 }}>
            <div style={{ fontSize:12.5, fontWeight:700, color:c.white, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.name || 'Пользователь'}</div>
            <div style={{ fontSize:10.5, color:c.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:"'JetBrains Mono',monospace" }}>{user?.email || ''}</div>
          </div>
        </div>

        <button onClick={() => t.toggle()} style={{
          display:'flex', alignItems:'center', gap:10, padding:'8px 14px', borderRadius:10,
          border:'none', width:'100%', background:'transparent', color:c.muted,
          fontSize:13.5, fontWeight:600, cursor:'pointer',
          fontFamily:"'Plus Jakarta Sans','Manrope',sans-serif", transition:'all .18s', marginBottom:1,
        }}
        onMouseEnter={e => { e.currentTarget.style.background = t.dark ? 'rgba(255,255,255,.05)' : 'rgba(10,32,40,.05)'; e.currentTarget.style.color = c.text }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = c.muted }}>
          <Ic n={t.dark ? 'sun' : 'moon'} s={15}/>{t.dark ? 'Светлая тема' : 'Тёмная тема'}
        </button>

        <button onClick={handleLogout} style={{
          display:'flex', alignItems:'center', gap:10, padding:'8px 14px', borderRadius:10,
          border:'none', width:'100%', background:'transparent', color:'rgba(248,113,113,.7)',
          fontSize:13.5, fontWeight:600, cursor:'pointer',
          fontFamily:"'Plus Jakarta Sans','Manrope',sans-serif", transition:'all .18s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(248,113,113,.07)'; e.currentTarget.style.color = '#f87171' }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(248,113,113,.7)' }}>
          <Ic n="logout" s={15}/>Выйти
        </button>
      </div>
    </div>
  )

  const goNewProject = () => navigateToNewProject(navigate)
  const sharedProps = { navigate, c, goNewProject }
  const gridColor = t.dark ? 'rgba(31,200,214,.052)' : 'rgba(10,123,134,.09)'

  return (
    <div data-theme={t.dark ? 'dark' : 'light'} style={{
      display: 'flex', minHeight: '100vh', color: c.text, overflowX: 'hidden', maxWidth: '100vw',
      fontFamily: "'Plus Jakarta Sans','Manrope',system-ui,sans-serif", WebkitFontSmoothing: 'antialiased',
      backgroundColor: c.bg0,
      backgroundImage: t.dark
        ? `linear-gradient(${gridColor} 1px,transparent 1px),linear-gradient(90deg,${gridColor} 1px,transparent 1px)`
        : `linear-gradient(${gridColor} 1px,transparent 1px),linear-gradient(90deg,${gridColor} 1px,transparent 1px),linear-gradient(145deg,#E0F6FA 0%,#F2FAFB 45%,#E4F5F9 100%)`,
      backgroundSize: t.dark ? '54px 54px' : '54px 54px, 54px 54px, 100% 100%',
      backgroundAttachment: 'local',
    }}>
      <style>{`
        *{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;text-rendering:optimizeLegibility;box-sizing:border-box}

        @keyframes wl-pulse{0%,100%{opacity:1}50%{opacity:.45}}
        @keyframes wl-toast{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes wlMenuIn{from{opacity:0;transform:translateY(-10px) scale(.96)}to{opacity:1;transform:translateY(0) scale(1)}}

        /* Базовые размеры — вынесены из inline для надёжного переопределения */
        .wl-main{margin-left:280px;min-width:0;overflow-x:hidden}
        .wl-acct-wrap{width:252px}
        .wl-acct-dropdown{width:252px}

        /* Grid helpers */
        .wl-projects-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:16px}
        .wl-2col{display:grid;grid-template-columns:1fr 1fr;gap:16px}
        .wl-form-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px}
        .wl-form-grid>*{min-width:0;position:relative}
        .wl-info-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}
        .wl-create-grid{display:grid;grid-template-columns:1fr 1fr;gap:0;padding-bottom:4px}
        .wl-main-pad{padding:28px 24px}
        .wl-stats-wrap{display:flex;flex-wrap:wrap;gap:12px}
        .wl-services-row{display:grid;grid-template-columns:repeat(5,1fr);gap:12px}
        .wl-burger{display:none}

        /* Tablet ≤1100px: sidebar → drawer */
        @media(max-width:1100px){
          .wl-sidebar{display:none!important}
          .wl-burger{display:flex!important}
          .wl-main{margin-left:0}
          .wl-create-btn{display:none!important}
          .wl-acct-wrap{width:auto}
          .wl-2col{grid-template-columns:1fr}
          .wl-create-grid{grid-template-columns:1fr}
          .wl-services-row .wl-svc-card{padding:13px!important}
        }

        /* Tablet ≤820px */
        @media(max-width:820px){
          .wl-main-pad{padding:20px 18px}
          .wl-header{padding:0 16px!important}
          .wl-theme-txt{display:none!important}
          .wl-account-email{display:none!important}
          .wl-services-row{grid-template-columns:repeat(3,1fr)}
          .wl-about-hero-pad{padding:24px 20px 20px!important}
          .wl-about-inner-pad{padding:20px 20px 0!important}
          .wl-about-cta-pad{padding:20px 20px 22px!important}
        }

        /* Phone ≤600px */
        @media(max-width:600px){
          .wl-main-pad{padding:14px 12px}
          .wl-header{padding:0 12px!important}
          .wl-form-grid{grid-template-columns:1fr}
          .wl-info-grid{grid-template-columns:1fr}
          .wl-stats-wrap{display:grid!important;grid-template-columns:1fr 1fr;gap:8px!important}
          .wl-services-row{grid-template-columns:repeat(2,1fr)}
          .wl-projects-grid{grid-template-columns:1fr}
          .wl-topbar-right{gap:6px!important}
          .wl-account-name{display:none!important}
          .wl-account-email{display:none!important}
          .wl-acct-dropdown{width:min(252px,calc(100vw - 24px))}
          .wl-about-hero-pad{padding:18px 14px 16px!important}
          .wl-about-inner-pad{padding:16px 14px 0!important}
          .wl-about-cta-pad{padding:16px 14px 18px!important}
          .wl-theme-btn{display:none!important}
        }

        /* Small phone ≤480px */
        @media(max-width:480px){
          .wl-header{padding:0 10px!important}
          .wl-main-pad{padding:12px 10px}
          .wl-projects-grid{gap:10px}
        }

        /* На мобиле скрываем переходы в приложение */
        @media(max-width:1100px){.wl-app-only{display:none!important}}

        /* Very small ≤380px */
        @media(max-width:380px){
          .wl-stats-wrap{grid-template-columns:1fr!important}
          .wl-services-row{grid-template-columns:1fr}
          .wl-info-grid{grid-template-columns:1fr}
          .wl-topbar-right{gap:4px!important}
        }
      `}</style>

      {/* Desktop sidebar */}
      <aside className="wl-sidebar" style={{
        width: 280, position: 'fixed', top: 0, left: 0, height: '100vh',
        background: c.bg1, borderRight: `1px solid ${c.line}`,
        display: 'flex', flexDirection: 'column', zIndex: 50, overflowY: 'auto',
        boxShadow: t.dark ? 'none' : '4px 0 28px rgba(10,80,100,.1)',
      }}>
        {renderSidebar()}
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99, background: 'rgba(0,0,0,.55)' }} onClick={() => setDrawerOpen(false)}>
          <aside style={{ width: 280, height: '100%', background: c.bg1, borderRight: `1px solid ${c.line}` }} onClick={e => e.stopPropagation()}>
            {renderSidebar()}
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="wl-main" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Topbar */}
        <header className="wl-header" style={{
          height: 60, borderBottom: `1px solid ${c.line}`, background: c.bg1,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 24px', position: 'sticky', top: 0, zIndex: 40,
          boxShadow: t.dark ? 'none' : '0 1px 0 rgba(10,32,40,.04), 0 4px 20px rgba(10,80,100,.07)',
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0, flex:1, overflow:'hidden' }}>
            <button className="wl-burger" onClick={() => setDrawerOpen(true)} style={{
              background: t.dark ? 'rgba(255,255,255,.06)' : 'rgba(10,32,40,.06)',
              border: `1px solid ${c.line}`, borderRadius:9,
              color:c.text, cursor:'pointer', padding:'6px', display:'none', flexShrink:0,
            }}>
              <Ic n="menu" s={18}/>
            </button>
            <div style={{ minWidth:0, overflow:'hidden' }}>
              <h1 style={{ fontSize:16, fontWeight:800, color:c.white, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', letterSpacing:'-.015em', lineHeight:1.2 }}>{tabTitle}</h1>
              {tab === 'projects' && (
                <div style={{ fontSize:11.5, color:c.muted, fontFamily:"'JetBrains Mono',monospace", whiteSpace:'nowrap', marginTop:1 }}>
                  {projects.length} {projects.length === 1 ? 'проект' : projects.length < 5 ? 'проекта' : 'проектов'}
                </div>
              )}
            </div>
          </div>
          <div className="wl-topbar-right" style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 8 }}>
            <button className="wl-create-btn" onClick={goNewProject} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', borderRadius: 999, border: 'none',
              background: `linear-gradient(135deg,${c.tealBright},${c.tealDeep})`,
              color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif", whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              <Ic n="plus" s={14}/><span className="wl-create-txt">Создать проект</span>
            </button>
            <button className="wl-theme-btn" onClick={() => t.toggle()} style={{
              background: `rgba(15,163,177,.1)`, border: `1px solid rgba(15,163,177,.2)`,
              borderRadius: 9, padding: '6px 10px', color: c.teal,
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif",
              display: 'flex', alignItems: 'center', gap: 6, transition: 'all .2s', whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              <Ic n={t.dark ? 'sun' : 'moon'} s={14}/><span className="wl-theme-txt">{t.dark ? 'Светлая' : 'Тёмная'}</span>
            </button>
            <div className="wl-acct-wrap" style={{ position: 'relative', flexShrink: 0 }}>
              {/* Trigger */}
              <button onClick={() => setAcctMenu(v => !v)} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                width: '100%', padding: '4px 12px 4px 4px', borderRadius: 999,
                border: acctMenu
                  ? `1px solid ${t.dark ? 'rgba(31,200,214,.55)' : 'rgba(10,123,134,.5)'}`
                  : `1px solid ${c.line}`,
                background: acctMenu ? (t.dark ? 'rgba(15,163,177,.1)' : 'rgba(15,163,177,.07)') : c.bg1,
                cursor: 'pointer', transition: 'all .22s',
                boxShadow: acctMenu ? `0 0 0 3px ${t.dark ? 'rgba(15,163,177,.14)' : 'rgba(10,123,134,.1)'}` : 'none',
              }}
              onMouseEnter={e => { if (!acctMenu) { e.currentTarget.style.borderColor = c.teal; e.currentTarget.style.background = t.dark ? 'rgba(15,163,177,.08)' : 'rgba(15,163,177,.06)' }}}
              onMouseLeave={e => { if (!acctMenu) { e.currentTarget.style.borderColor = c.line; e.currentTarget.style.background = c.bg1; e.currentTarget.style.boxShadow = 'none' }}}>
                <Avatar name={user?.name || ''} s={30}/>
                <div style={{ minWidth: 0, flex: 1 }} className="wl-account-name">
                  <div style={{ fontSize: 13, fontWeight: 700, color: c.white, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.25 }}>
                    {user?.name || 'Профиль'}
                  </div>
                  <div className="wl-account-email" style={{ fontSize: 10.5, color: c.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.25, fontFamily: "'JetBrains Mono',monospace" }}>
                    {user?.email || ''}
                  </div>
                </div>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  style={{ color: c.muted, transition: 'transform .22s', transform: acctMenu ? 'rotate(180deg)' : 'none', flexShrink: 0 }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>

              {acctMenu && (
                <>
                  <div onClick={() => setAcctMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 48 }}/>
                  <div className="wl-acct-dropdown" style={{
                    position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                    background: t.dark ? 'rgba(5,12,24,.82)' : 'rgba(250,253,255,.92)',
                    backdropFilter: 'blur(36px) saturate(160%)',
                    WebkitBackdropFilter: 'blur(36px) saturate(160%)',
                    border: t.dark ? '1px solid rgba(255,255,255,.11)' : '1px solid rgba(10,123,134,.18)',
                    borderRadius: 18, overflow: 'hidden',
                    boxShadow: t.dark
                      ? '0 12px 40px rgba(0,0,0,.55), inset 0 1px 0 rgba(255,255,255,.1)'
                      : '0 8px 32px rgba(10,60,80,.16), inset 0 1px 0 rgba(255,255,255,.95)',
                    zIndex: 49,
                    animation: 'wlMenuIn .2s cubic-bezier(.16,1,.3,1)',
                  }}>
                    {/* Teal accent line */}
                    <div style={{ height: 1.5, background: 'linear-gradient(90deg,transparent,rgba(31,200,214,.7) 35%,rgba(15,163,177,.7) 65%,transparent)' }}/>

                    {/* User header */}
                    <button onClick={() => { goTab('profile'); setAcctMenu(false) }} style={{
                      display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                      padding: '11px 12px 9px', border: 'none', background: 'transparent',
                      cursor: 'pointer', textAlign: 'left', transition: 'background .15s',
                      fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = t.dark ? 'rgba(255,255,255,.05)' : 'rgba(15,163,177,.06)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <Avatar name={user?.name || ''} s={36}/>
                        <div style={{ position: 'absolute', bottom: 1, right: 0, width: 8, height: 8, borderRadius: '50%', background: '#22c55e', border: t.dark ? '1.5px solid rgba(5,12,24,.9)' : '1.5px solid rgba(250,253,255,.9)', boxShadow: '0 0 5px rgba(34,197,94,.7)' }}/>
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: c.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: '-.015em' }}>{user?.name || 'Пользователь'}</div>
                        <div style={{ fontSize: 10.5, color: t.dark ? 'rgba(180,210,230,.72)' : 'rgba(10,80,100,.6)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1.5, fontFamily: "'JetBrains Mono',monospace" }}>{user?.email || ''}</div>
                      </div>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        style={{ color: t.dark ? 'rgba(255,255,255,.25)' : 'rgba(0,0,0,.2)', flexShrink: 0 }}>
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </button>

                    {/* Divider */}
                    <div style={{ height: 1, background: t.dark ? 'linear-gradient(90deg,transparent,rgba(255,255,255,.1) 30%,rgba(255,255,255,.1) 70%,transparent)' : 'linear-gradient(90deg,transparent,rgba(10,123,134,.18) 30%,rgba(10,123,134,.18) 70%,transparent)', margin: '0 10px 4px' }}/>

                    {/* Menu items */}
                    <div style={{ padding: '0 6px 6px' }}>
                      {[
                        { label: 'На сайт',        sub: 'Вернуться на главную', ic: 'home',   action: () => { navigate('/'); setAcctMenu(false) },
                          icBg: t.dark ? 'linear-gradient(135deg,rgba(31,200,214,.18),rgba(10,123,134,.1))' : 'rgba(10,123,134,.1)',
                          icBorder: t.dark ? 'rgba(31,200,214,.28)' : 'rgba(10,123,134,.2)', icColor: t.dark ? '#1FC8D6' : '#0A7B86' },
                        { label: 'Мои проекты',    sub: `${projects.length} проект${projects.length === 1 ? '' : projects.length < 5 ? 'а' : 'ов'}`, ic: 'folder', action: () => { goTab('projects'); setAcctMenu(false) },
                          icBg: t.dark ? 'linear-gradient(135deg,rgba(99,102,241,.18),rgba(67,56,202,.1))' : 'rgba(99,102,241,.1)',
                          icBorder: t.dark ? 'rgba(99,102,241,.28)' : 'rgba(99,102,241,.22)', icColor: t.dark ? '#818cf8' : '#6366f1' },
                        { label: 'Создать проект', sub: 'Открыть конструктор',  ic: 'plus',   action: () => { setAcctMenu(false); goNewProject() }, appOnly: true,
                          icBg: t.dark ? 'linear-gradient(135deg,rgba(34,197,94,.18),rgba(22,163,74,.1))' : 'rgba(34,197,94,.1)',
                          icBorder: t.dark ? 'rgba(34,197,94,.28)' : 'rgba(34,197,94,.22)', icColor: t.dark ? '#4ade80' : '#16a34a' },
                      ].map((item, i) => (
                        <button key={item.label} onClick={item.action} className={item.appOnly ? 'wl-app-only' : ''} style={{
                          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                          padding: '6px 8px', borderRadius: 10, border: 'none',
                          background: 'transparent', cursor: 'pointer', textAlign: 'left',
                          fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif", transition: 'background .15s',
                          marginBottom: i < 2 ? 1 : 0,
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = t.dark ? 'rgba(255,255,255,.07)' : 'rgba(15,163,177,.07)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <div style={{ width: 30, height: 30, borderRadius: 9, flexShrink: 0, background: item.icBg, border: `1px solid ${item.icBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: item.icColor }}>
                            <Ic n={item.ic} s={14}/>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: c.white, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</div>
                            <div style={{ fontSize: 10.5, color: t.dark ? 'rgba(180,210,230,.6)' : 'rgba(10,80,100,.55)', lineHeight: 1.3, marginTop: 1 }}>{item.sub}</div>
                          </div>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                            style={{ color: t.dark ? 'rgba(255,255,255,.22)' : 'rgba(0,0,0,.18)', flexShrink: 0 }}>
                            <polyline points="9 18 15 12 9 6"/>
                          </svg>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="wl-main-pad" style={{ flex: 1, width: '100%', boxSizing: 'border-box' }}>
          {tab === 'profile' && (
            <ProfileTab user={user} projects={projects} projectsLoading={projectsLoading}
              resolveAssetUrl={resolveAssetUrl} projectNumberMap={projectNumberMap} onGoProjects={() => goTab('projects')} {...sharedProps}/>
          )}
          {tab === 'projects' && (
            <ProjectsTab projects={projects} projectsLoading={projectsLoading}
              onDelete={handleDeleteProject} onDownloadPdf={downloadProjectPdf} pdfDownloadingId={pdfDownloadingId}
              resolveAssetUrl={resolveAssetUrl} projectNumberMap={projectNumberMap} {...sharedProps}/>
          )}
          {tab === 'settings' && (
            <SettingsTab user={user} triggerToast={triggerToast} onProfileUpdate={handleProfileUpdate} c={c}/>
          )}
          {tab === 'about' && <AboutTab {...sharedProps}/>}
          {tab === 'review' && <ReviewTab user={user} c={c}/>}
        </main>
      </div>

      {/* Toast */}
      {toast?.open && (
        <div style={{
          position: 'fixed', bottom: 28, left: 0, right: 0,
          display: 'flex', justifyContent: 'center',
          pointerEvents: 'none', zIndex: 999,
        }}>
          <div style={{
            animation: 'wl-toast .25s ease',
            pointerEvents: 'auto',
            padding: '11px 22px', borderRadius: 12,
            background: toast.tone === 'ok'
              ? `linear-gradient(135deg,${c.tealBright},${c.tealDeep})`
              : toast.tone === 'info'
                ? 'linear-gradient(135deg,#F59E0B,#D97706)'
                : 'linear-gradient(135deg,#EF4444,#DC2626)',
            color: '#fff', fontSize: 14, fontWeight: 700,
            boxShadow: '0 8px 28px rgba(0,0,0,.35)',
            display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap',
          }}>
            <Ic n={toast.tone === 'ok' ? 'check' : toast.tone === 'info' ? 'download' : 'info'} s={15}/>{toast.message}
          </div>
        </div>
      )}
    </div>
  )
}
